import React, { useEffect, useState } from "react";
import {
  Outlet,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft2,
  ArrowRight2,
  CloseCircle,
  Edit,
  InfoCircle,
  Refresh,
  SearchNormal,
  Sort,
} from "iconsax-react";
import { getToken } from "../../../utils/token";
import instance from "../../../axios/instance";
import CreateOrder from "../../../components/modals/order/CreateOrder";
import { PaginationButton } from "../../../components/PaginationButton";
import {
  NewOrder,
  RefillOrder,
} from "../../../components/order_types/order_types";
import { Tooltip } from "react-tooltip";
import { ArrowUpNarrowWide, CheckCheck, RotateCcw, Undo2 } from "lucide-react";
import { getTableSortOrder } from "../../../utils/data";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;

const OrderStatusIndicators = ({ order }) => {
  const isCanceled = order.order_status?.toLowerCase() === "canceled";
  const isRejected = order.order_status?.toLowerCase() === "rejected";
  const hasCancellationRequest = order.cancellation_request != null;

  if (!isCanceled && !hasCancellationRequest && !isRejected) {
    return null;
  }

  return (
    <div className="inline-flex shrink-0 items-center gap-1">
      {isCanceled && (
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-800"
          data-tooltip-id="order-canceled"
          data-tooltip-content="Cancelled"
        >
          <CloseCircle size={16} variant="Outline" />
        </span>
      )}
      {hasCancellationRequest && (
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-800"
          data-tooltip-id="order-cancellation-request"
          data-tooltip-content="Cancellation Request"
        >
          <InfoCircle size={16} variant="Outline" />
        </span>
      )}
      {isRejected && (
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-800"
          data-tooltip-id="order-rejected"
          data-tooltip-content="Order Rejected"
        >
          <RotateCcw size={16} variant="Outline" />
        </span>
      )}
    </div>
  );
};

export const allOrdersLoader = async ({ request }) => {
  const token = getToken();
  const params = new URLSearchParams(request.url.split("?")[1]);
  const pageIndex = params.get("page") ? Number(params.get("page")) : 0;
  const qt = params.get("query") || "";
  const orders = await instance.get(
    `/doctor/orders?qt=${qt}&page_index=${pageIndex}&page_size=10`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return {
    orders: orders.data,
  };
};

const AllOrdersPage = () => {
  let { orders: _orders } = useLoaderData();
  const orders = _orders.orders;
  const defSort = _orders.sort || "";
  const [params, setParams] = useSearchParams();
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const pageIndex = params.get("page") ? Number(params.get("page")) : 0;
  const [tableOrder, setTableOrder] = useState(
    getTableSortOrder(defSort) || "",
  );
  const { revalidate } = useRevalidator();
  const navigate = useNavigate();

  useEffect(() => {
    setTableOrder(getTableSortOrder(defSort) || "");
  }, [defSort]);

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setParams({ query, page: 0 });
  };

  const handleSortSave = async (sort) => {
    const token = getToken();
    try {
      await instance.post(
        `/doctor/table/sort`,
        {
          tableName: "all-orders",
          sort,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setParams((prev) => ({
        ...Object.fromEntries(prev),
        page: 0,
      }));
    } catch (error) {
      toast.error(
        error.response ? error.response.data.message : "Failed to sort orders",
      );
    }
  };

  const handleSort = async (def) => {
    const currentSort = defSort || "";
    const nextSort = currentSort === def ? "" : def;

    const sortOrder = def.split(":")[1];
    setTableOrder(sortOrder);
    await handleSortSave(nextSort);
  };

  const handleSortChange = (sortKey) => {
    let sort = sortKey;
    if (tableOrder === "asc") {
      sort = `${sortKey}:desc`;
    } else if (tableOrder === "desc") {
      sort = ``;
    } else {
      sort = `${sortKey}:asc`;
    }
    handleSort(sort);
  };

  const handleParamChange = (key, value) => {
    setParams((prev) => ({
      ...Object.fromEntries(prev),
      [key]: value,
    }));
  };

  return (
    <>
      <div className="px-7 py-5 bg-[#fafafa]">
        {isCreateOrderModalOpen && (
          <CreateOrder
            isOpen={Boolean(isCreateOrderModalOpen)}
            onClose={() => setIsCreateOrderModalOpen(false)}
            triggerRevalidate={() => {
              revalidate();
            }}
            medType={isCreateOrderModalOpen}
          />
        )}
        {/* {editOrder && (
        <EditOrder
          isOpen={Boolean(editOrder)}
          onClose={() => setEditOrder(null)}
          triggerRevalidate={() => location.reload()}
          order={editOrder}
        />
      )} */}

        <div className="flex justify-between items-center">
          <div className="font-bold text-2xl">All Orders ({_orders.total})</div>

          <div className="flex gap-2 items-center">
            <button
              className="bg-primary py-2 px-3 shadow-sm rounded-[4px] font-medium text-[14px] text-white"
              onClick={() => {
                setIsCreateOrderModalOpen("compound");
              }}
            >
              Create Compound Order +
            </button>
            <button
              className="bg-primary py-2 px-3 shadow-sm rounded-[4px] font-medium text-[14px] text-white"
              onClick={() => setIsCreateOrderModalOpen("drug")}
            >
              Create Order +
            </button>
          </div>
        </div>

        <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
          <div className="flex justify-between p-2 items-center">
            <form className="relative" onSubmit={handleSearch}>
              <input
                className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
                placeholder="Search order by ID"
                name="query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
                type="submit"
              >
                <SearchNormal size={15} />
              </button>
            </form>

            <div className="flex gap-2 items-center">
              <PaginationButton
                item={<ArrowLeft2 size={16} />}
                disabled={pageIndex <= 0}
                handleClick={() => handleParamChange("page", pageIndex - 1)}
              />
              <span className="font-light text-xs">Page {pageIndex + 1}</span>
              <PaginationButton
                item={<ArrowRight2 size={16} />}
                disabled={pageIndex + 1 >= Math.ceil(_orders.total / PAGE_SIZE)}
                handleClick={() => handleParamChange("page", pageIndex + 1)}
              />
            </div>
          </div>
          <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
            <table className="w-full relative whitespace-nowrap">
              <thead>
                <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                  <td className="px-6 py-4">#</td>
                  <td className="px-6 py-4">Order ID</td>
                  <td
                    className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                    onClick={() => handleSortChange("order_type")}
                  >
                    Type{" "}
                    {defSort.includes("order_type") ? (
                      <ArrowUpNarrowWide
                        size="15"
                        className={`${
                          defSort.includes("desc") ? "rotate-180" : "rotate-0"
                        } inline`}
                        color="#FF8A65"
                      />
                    ) : (
                      <Sort size="15" className={`inline`} color="#FF8A65" />
                    )}
                  </td>
                  <td className="px-6 py-4">Shipping service</td>
                  <td className="w-[40px] px-1 py-2" data-tooltip-id="intake">
                    I
                  </td>
                  <td className="w-[40px] px-1 py-2" data-tooltip-id="ppv1">
                    PPV1
                  </td>
                  <td className="w-[40px] px-1 py-2" data-tooltip-id="pv1">
                    PV1
                  </td>
                  <td
                    className="w-[40px] px-1 py-2"
                    data-tooltip-id="fulfillment"
                  >
                    F
                  </td>
                  <td className="w-[40px] px-1 py-2" data-tooltip-id="pv2">
                    PV2
                  </td>
                  <td className="w-[40px] px-1 py-2" data-tooltip-id="shipping">
                    S
                  </td>
                  <td
                    className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                    onClick={() => handleSortChange("payment_status")}
                  >
                    Payment Status{" "}
                    {defSort.includes("payment_status") ? (
                      <ArrowUpNarrowWide
                        size="15"
                        className={`${
                          defSort.includes("desc") ? "rotate-180" : "rotate-0"
                        } inline`}
                        color="#FF8A65"
                      />
                    ) : (
                      <Sort size="15" className={`inline`} color="#FF8A65" />
                    )}
                  </td>
                  <td
                    className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                    onClick={() => handleSortChange("created_at")}
                  >
                    Date{" "}
                    {defSort.includes("created_at") ? (
                      <ArrowUpNarrowWide
                        size="15"
                        className={`${
                          defSort.includes("desc") ? "rotate-180" : "rotate-0"
                        } inline`}
                        color="#FF8A65"
                      />
                    ) : (
                      <Sort size="15" className={`inline`} color="#FF8A65" />
                    )}
                  </td>
                  <td></td>
                </tr>
              </thead>

              <tbody>
                {orders.length < 1 ? (
                  <tr>
                    <td className="px-6 py-4" colSpan={6}>
                      No recent orders
                    </td>
                  </tr>
                ) : (
                  orders.map((order, index) => (
                    <tr key={order.id} className="h-14 text-[14px] border-b">
                      <td className="px-6 py-4">
                        {pageIndex * PAGE_SIZE + index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <span>{order.id}</span>
                          <OrderStatusIndicators order={order} />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {order.order_type === "Refill" ? (
                          <RefillOrder />
                        ) : (
                          <NewOrder />
                        )}
                      </td>
                      <td className="px-6 py-4 capitalize">
                        {order.shipping_service
                          ? JSON.parse(order.shipping_service).name
                          : "--"}
                      </td>
                      {order.steps.map((step) => (
                        <td
                          className="px-1 py-2 capitalize w-[40px]"
                          key={step.step}
                        >
                          {step.is_done ? (
                            <CheckCheck size={16} color="green" />
                          ) : (
                            "-"
                          )}
                        </td>
                      ))}
                      <td className="px-6 py-4 capitalize">
                        {order.payment_status}
                      </td>
                      <td className="px-6 py-4">
                        {
                          new Date(order.created_at)
                            .toLocaleString()
                            .split(",")[0]
                        }
                      </td>
                      <td
                        onClick={() =>
                          navigate(`/dashboard/orders/${order.id}`)
                        }
                        className="px-6 py-4 underline text-primary cursor-pointer"
                      >
                        View order
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Outlet />
      </div>
      <Tooltip
        content="Intake"
        place="top"
        effect="solid"
        id="intake"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Pre. PV1"
        place="top"
        effect="solid"
        id="ppv1"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="PV1"
        place="top"
        effect="solid"
        id="pv1"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Fulfillment"
        place="top"
        effect="solid"
        id="fulfillment"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="PV2"
        place="top"
        effect="solid"
        id="pv2"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Shipping"
        place="top"
        effect="solid"
        id="shipping"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Cancelled"
        place="top"
        effect="solid"
        id="order-canceled"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Cancellation Request"
        place="top"
        effect="solid"
        id="order-cancellation-request"
        className="!text-xs !font-normal"
      />
      <Tooltip
        place="top"
        effect="solid"
        id="order-rejected"
        className="!text-xs !font-normal"
      />
    </>
  );
};

export default AllOrdersPage;
